import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function Sel({ label, value, onChange, options, testid, placeholder }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-slate-900/60 border-white/10 h-9">
          <SelectValue placeholder={placeholder || "Select…"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function DateField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">{label}</Label>
      <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-slate-900/60 border-white/10 h-9 font-mono" />
    </div>
  );
}

const EMPTY = {
  patient_name: "", mrn: "", source_line: "", receiving_line: "", referral_source: "",
  location: "", receiving_contact: "", receiving_contact_email: "", status: "New", refer_date: "", recheck_date: "",
  owner: "", close_date: "", reason_code: "", completion_type: "", pending_reason: "", note: "",
};

export default function ReferralFormDialog({ open, onOpenChange, config, initial, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial, note: "" } : { ...EMPTY });
      setFiles([]);
      api.get("/contacts").then((r) => setContacts(r.data.filter((c) => c.active))).catch(() => setContacts([]));
    }
  }, [open, initial]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isClosing = form.status === "Completed" || form.status === "Not Completed";
  const isMonitor = form.status === "Monitoring" || form.status === "On-hold";
  const matching = contacts.filter((c) => c.receiving_line === form.receiving_line && (!c.location || c.location === form.location));
  const pickContact = (email) => {
    const c = contacts.find((x) => x.email === email);
    if (c) setForm((f) => ({ ...f, receiving_contact: c.name, receiving_contact_email: c.email }));
  };

  const uploadAttachments = async (refId) => {
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/referrals/${refId}/attachments`, fd);
      } catch {
        toast.error(`Couldn't attach ${f.name}`);
      }
    }
  };

  const validateFiles = (list) => {
    const MAX_SIZE = 15 * 1024 * 1024, MAX_FILES = 20;
    let out = list.filter((f) => {
      if (f.size > MAX_SIZE) { toast.error(`${f.name} exceeds 15 MB — skipped`); return false; }
      return true;
    });
    if (out.length > MAX_FILES) {
      toast.error(`Up to ${MAX_FILES} files at once — keeping the first ${MAX_FILES}`);
      out = out.slice(0, MAX_FILES);
    }
    return out;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (isEdit) {
        await api.put(`/referrals/${initial.id}`, payload);
        toast.success(`${initial.id} updated`);
      } else {
        let created;
        try {
          const { data } = await api.post("/referrals", payload);
          created = data;
        } catch (err) {
          const d = err.response?.data?.detail;
          if (err.response?.status === 409 && d?.code === "duplicate") {
            const list = (d.matches || []).map((m) => `• ${m.id} — ${m.lane} (${m.status}, matched on ${m.matched_on})`).join("\n");
            if (!window.confirm(`Possible duplicate — an open referral already exists for this patient:\n\n${list}\n\nCreate this referral anyway?`)) {
              setSaving(false);
              return;
            }
            const { data } = await api.post("/referrals?force=true", payload);
            created = data;
          } else {
            throw err;
          }
        }
        if (created) {
          if (files.length) await uploadAttachments(created.id);
          toast.success(`${created.id} created${files.length ? ` · ${files.length} file(s) attached` : ""}`);
        }
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return null;
  const receivingOpts = config.receiving_lines || [...config.service_lines, ...config.uncounted_destinations];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="referral-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">{isEdit ? `Edit ${initial.id}` : "New Referral"}</DialogTitle>
          <DialogDescription className="text-xs font-mono text-muted-foreground">
            Refer Date required · status changes are recorded to the audit log.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Patient Name (PHI)</Label>
            <Input value={form.patient_name} onChange={(e) => set("patient_name")(e.target.value)} data-testid="field-patient-name" className="bg-slate-900/60 border-white/10 h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">MRN (PHI)</Label>
            <Input value={form.mrn} onChange={(e) => set("mrn")(e.target.value)} data-testid="field-mrn" className="bg-slate-900/60 border-white/10 h-9 font-mono" />
          </div>
          <Sel label="Source Line" value={form.source_line} onChange={set("source_line")} options={config.service_lines} testid="field-source-line" />
          <Sel label="Receiving Line" value={form.receiving_line} onChange={set("receiving_line")} options={receivingOpts} testid="field-receiving-line" />
          <Sel label="Referral Source" value={form.referral_source} onChange={set("referral_source")} options={config.referral_sources} testid="field-referral-source" />
          <Sel label="Location" value={form.location} onChange={set("location")} options={config.locations} testid="field-location" />
          <DateField label="Refer Date *" value={form.refer_date} onChange={set("refer_date")} testid="field-refer-date" />
          <Sel label="Status" value={form.status} onChange={set("status")} options={config.all_statuses} testid="field-status" />
          <div className="space-y-1 col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Assign from Directory {form.receiving_line ? `(${form.receiving_line})` : ""}</Label>
            <select value={form.receiving_contact_email || ""} onChange={(e) => pickContact(e.target.value)} data-testid="field-contact-directory"
                    className="w-full h-9 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm">
              <option value="">{matching.length ? "Select a contact…" : "No directory contacts for this line — type below"}</option>
              {matching.map((c) => <option key={c.id} value={c.email}>{c.name} — {c.email}{c.location ? ` (${c.location})` : ""}</option>)}
            </select>
            <p className="text-[11px] font-mono text-muted-foreground" data-testid="lane-routing-hint">
              {isEdit
                ? "Changing the contact here reassigns ownership (owner, coordinator or admin only)."
                : `Every contact covering this lane is emailed${matching.length ? ` (${matching.length})` : ""}; the first to respond takes ownership. Picking someone adds them to the lane.`}
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Receiving Contact</Label>
            <Input value={form.receiving_contact} onChange={(e) => set("receiving_contact")(e.target.value)} data-testid="field-receiving-contact" className="bg-slate-900/60 border-white/10 h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Contact Email (for notifications)</Label>
            <Input type="email" value={form.receiving_contact_email} onChange={(e) => set("receiving_contact_email")(e.target.value)} data-testid="field-receiving-contact-email" className="bg-slate-900/60 border-white/10 h-9" />
          </div>

          {!isEdit && (
            <div className="space-y-1 col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Attach documents (optional)</Label>
              <input type="file" multiple data-testid="field-attachments"
                     onChange={(e) => setFiles(validateFiles(Array.from(e.target.files || [])))}
                     accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt,.doc,.docx,.xls,.xlsx"
                     className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-cyan-500 file:text-slate-950 file:font-semibold file:px-3 file:py-1.5 file:cursor-pointer" />
              {files.length > 0 && <p className="text-xs font-mono text-cyan-300" data-testid="attachments-selected">{files.length} file(s) selected · up to 20 · uploaded after the referral is created</p>}
            </div>
          )}

          {isMonitor && (
            <>
              <DateField label="Re-check Date *" value={form.recheck_date} onChange={set("recheck_date")} testid="field-recheck-date" />
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Owner *</Label>
                <Input value={form.owner} onChange={(e) => set("owner")(e.target.value)} data-testid="field-owner" className="bg-slate-900/60 border-white/10 h-9" />
              </div>
            </>
          )}
          {isClosing && <DateField label="Close Date *" value={form.close_date} onChange={set("close_date")} testid="field-close-date" />}
          {form.status === "Pending Disposition" && (
            <>
              <Sel label="Pending On *" value={form.pending_reason} onChange={set("pending_reason")} options={config.pending_reasons || []} testid="field-pending-reason" />
              <DateField label="Follow-up Date *" value={form.recheck_date} onChange={set("recheck_date")} testid="field-followup-date" />
            </>
          )}
          {form.status === "Not Completed" && <Sel label="Reason Code *" value={form.reason_code} onChange={set("reason_code")} options={config.reason_codes} testid="field-reason-code" />}
          {form.status === "Completed" && <Sel label="Completion (SOC/Admit)" value={form.completion_type} onChange={set("completion_type")} options={config.completion_types} testid="field-completion-type" />}
        </div>

        <div className="space-y-1 mt-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Add Note {isEdit ? "(resets aging clock)" : ""}</Label>
          {form.location === "Community" && (
            <p className="text-xs font-mono text-cyan-300" data-testid="community-note-hint">Patient isn't on a campus — add their home address here in the Note.</p>
          )}
          <Textarea value={form.note} onChange={(e) => set("note")(e.target.value)} data-testid="field-note" className="bg-slate-900/60 border-white/10" rows={2} />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="form-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-save-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create referral"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
